import { IPerRoomFinalEstimate } from "src/shared/backend/Utils/EstimateEngineUtils";
import { AttachmentFile, DocumentTableCell, DocumentWord, IWordWithConfidence, Region, TemplateCell } from "../Azure";
import { IUploadedFileProperties } from "../EngineUtils";
import { HeaderInfoItem, TicSheetType } from "../interfaces";
import { IQuantityFormula } from "./EngineSharedUtils";

export enum TPARuleTypes {
	RemoveComplement = "Remove Complement",
	ConvertToLikelyComplement = "Convert to likely complement",
	AdditionalComplements = "Additional complements", // Not used
	SplitUpLineItems = "Split up Line Items",
	RolledFlooringWasteCalculation = "Rolled Flooring Waste Calculation", // Not used
	AlternativeMaximum = "Alternative maximum",
	AlternateXactimateCoding = "Alternate Xactimate coding", // Not used
	ConRoomToConLab = "Convert Room to Con Lab or vice versa",
}

export enum DayOfWeek {
	SUN = "SUN",
	MON = "MON",
	TUE = "TUE",
	WED = "WED",
	THU = "THU",
	FRI = "FRI",
	SAT = "SAT",
}

export enum Holiday {
	NEW_YEARS_DAY = "New Year's Day",
	MARTIN_LUTHER_KING_JR_DAY = "Martin Luther King Jr. Day",
	PRESIDENTS_DAY = "Presidents' Day",
	MEMORIAL_DAY = "Memorial Day",
	INDEPENDENCE_DAY = "Independence Day",
	LABOR_DAY = "Labor Day",
	COLUMBUS_DAY = "Columbus Day",
	INDIGENOUS_PEOPLES_DAY = "Indigenous Peoples' Day",
	VETERANS_DAY = "Veterans Day",
	THANKSGIVING = "Thanksgiving",
	CHRISTMAS = "Christmas",
}

export const gTPARuleTypes = Object.values(TPARuleTypes);

export interface IEstimaticsData {
	estimateId: string;
	projectName: string;
	estimateReferenceNumber: string;
	requesterName: string;
	email: string;
	phone: string;
	propertyAddress: string;
	insuranceCarrier: string;
	claimNumber: string;
	tpa: string;
	dateCreated: string;
	createdByUserId: string;
	timestampCreated?: number;
	dateContacted: string;
	dateOfLoss: string;
	office: string;
	assignee: string;
	status: string;
	approximateClaim: string;
	links: string[];
	notes: string[];
	files: AttachmentFile[];
	category: string;
	typeOfLoss: string;
	fieldData: string;
	deleted: boolean;
	xactimateTransactionId?: string;
	uploadImagesFailed?: boolean;
	measurementData: any[];
	propertyAddressComponents: string;
	uploadedFilesProperties?: IUploadedFileProperties[];
	autoMergeRoomCompleted: boolean;
	docusketchId?: string;
	scopeNotes?: string;
	scopeNoteObjects?: ScopeNoteObject[];
	micaNotes?: string;
	clusterToRoomMappings?: ClusterToRoomMapping[];
	predictions?: Prediction[];
	estimateType?: EstimateType;
	mitigationDetails?: MitigationDetails;
	conversationIds?: string[];
	afterHoursServiceCall?: boolean;
	parentEstimateId?: string;
	creationSource?: EstimateCreationSource;
	referenceNumberType?: EstimateReferenceNumberType;
	taxRate?: number; // number between 0.0 and 1.0
	exports?: EstimateExport[];
	jobNimbusInfo?: JobNimbusInfo;
	cachedPDFPreview?: {
		categoryGrouped?: PDFPreviewData;
		roomGrouped?: PDFPreviewData;
	};
}

export enum ScopeNoteSource {
	DOCUSKETCH = "DOCUSKETCH",
	UPLOADED = "UPLOADED",
	VOICE = "VOICE",
	MICA = "MICA",
	CONTENTS = "CONTENTS",
	FREE_FORM_TIC_SHEET = "FREE_FORM_TIC_SHEET",
	XCELERATE = "XCELERATE",
}

export interface ScopeNoteObject {
	uuid: string;
	text: string;
	dateCreated: string;
	originalText?: string;
	source?: ScopeNoteSource;
	createdByUserId?: string;
	isMainScopeNote?: boolean;
	workAfterHours?: boolean;
	excludeFromEstimate?: boolean;
}

export interface MitigationDetails {
	workAfterHours?: boolean;
	cat3Contamination?: boolean; // DEPRECATED, use cat instead
	// refers to the category of water damage, should be 1, 2, or 3
	cat?: number;
	// Equipment count for a mitigation project
	// indexed by room name, lineItemString, timestamp
	equipmentCount?: EquipmentCount[];
}

export interface EquipmentCount {
	roomName: string;
	lineItemString: string;
	quantity: number;
	scopeNoteObjectUuid?: string;
	timeStamp?: number;
}
export interface IEstimateDataForUI {
	extractedImage?: IImageMetadata;
	estimateQueueItem?: IQueuedItemToProcess;
	metadata?: IEstimateMetadata[];
	dataForUI: IDocumentForUI;
}
export type TCellWithInputMap = { [s: string]: ITupleContentById | IFreeFormContentById };
export interface IMetadataForFinalEstimate {
	pageIndex?: number;
	roomName: string;
	pageId: string;
	pageName: string;
	sourceImageName: string;
	uploadId: string;
	fullPageImagePath: string;
	originalName: string;
	mergeWith?: string;
	uploadIdArray?: string[];
	fullImagePathArray?: string[];
	sourceImageNameArray?: string[];
	ticType?: TicSheetType | undefined;
	roomTypes?: RoomType[];
}

export interface JobNimbusInfo {
	address: {
		addressLine1?: string;
		addressLine2?: string;
		city?: string;
		stateText?: string;
		countryName?: string;
	};
	jobJnid: string;
	contactJnid: string;
	jobType: string;
	contactNumber: string;
}

export enum RoomType {
	STORAGE = "STORAGE",
	STAIRS = "STAIRS",
	PATIO__DECK = "PATIO/DECK",
	OFFICE = "OFFICE",
	MECHANICAL = "MECHANICAL",
	LAUNDRY = "LAUNDRY",
	KITCHEN = "KITCHEN",
	HALLWAY = "HALLWAY",
	GYM = "GYM",
	OTHER = "OTHER",
	GARAGE = "GARAGE",
	FAMILY_ROOM__LIVING_ROOM__DEN = "FAMILY ROOM/LIVING ROOM/DEN",
	EXTERIOR__ROOF = "EXTERIOR/ROOF",
	ENTRY__FOYER = "ENTRY/FOYER",
	DINING_ROOM = "DINING ROOM",
	CRAWL_SPACE = "CRAWL SPACE",
	CLOSET = "CLOSET",
	BEDROOM = "BEDROOM",
	BATHROOM = "BATHROOM",
	BASEMENT = "BASEMENT",
	ATTIC = "ATTIC",
}

export interface IDocumentForUI {
	// documentMetadata: IDocumentMetadata, // TODO ::
	metadata: IMetadataForFinalEstimate | null;
	itemForDebugOnly: IQueuedItemToProcess | null; // TODO :: remove when all the debug is done
	page: IPageForUI;
	roomName: string;
	originalName: string;
}
export interface IPageForUI {
	// pageMetadata: IPageMetadata, // TODO ::
	tuplesOutput: ITupleOutputForUI[];
	fullPageImagePath: string;
	mergeWith?: string;
	id: string;
}

export const FileErrorDummyPath = "~";

export interface IUploadFileData {
	mime: string;
	originalFileName: string;
	storagePath: string;
	uploadId?: string;
	uploadIndex?: number;
	deleted?: boolean;
	postUploadResults?: any[]; // this is used only for the UI. It's not saved in the database
}

export enum ESkipProcessingReason {
	NotTicSheet = "not-tic-sheet",
	UnsupportedTicType = "unsupported-tic-type",
	BadDocumentAngle = "bad-document-angle",
	NoCellsWithInput = "no-cells-with-input",
}
export interface ITicSheetHeaderInfo {
	address: string;
	roomName: string;
	dashNumber: string;
	debris: string[];
}
export interface IImageMetadata {
	id: string;
	estimatePath?: string;
	matchingRowsPath?: string;
	storagePath: string;
	originalName: string;
	classification?: string;
	mime: string;
	extractedFrom: string;
	layoutStoragePath?: string;
	readStoragePath?: string;
	cellsWithInputPath?: string;
	cellsRawDataPath?: string;
	skipProcessingReason?: ESkipProcessingReason;
	finalEstimatePath?: string;
	complementLineItemsPath?: string;
	ticInfo?: ITicSheetHeaderInfo;
	dataForUIPath?: string;
	pageId?: string;
	freeFormCellsPath?: string;
}
export interface IQueuedItemToProcess {
	organizationId: string;
	itemId: string;
	estimateId: string;
	tpa?: string;
	carrier?: string;
	originalUploadedFiles: IUploadFileData[];
	extractedImages?: IImageMetadata[];
	queueEntryTime: number;
	startProcessTime: number;
	endProcessTime: number;
	status: number;
	isParent?: boolean;
	autoMergeCompleted?: boolean;

	scopeNoteObjects?: ScopeNoteObject[];
	// If this is a scope note, we'll have text not files.
	uploadedText?: string;
	mitigationAnalysis?: string;
	scopeNoteUploadEnabled?: boolean;
	isAfterHoursServiceCall?: boolean;

	estimateType?: EstimateType;
	mitigationReports?: AttachmentFile[];
	contentTemplates?: AttachmentFile[];
	processorId: string;
	creatorId: string;
	lastUpdatedBy: string;
	isTest?: boolean;
	useCache?: boolean;
	pagesToProcess?: number[];
	itemIndex?: number;
	docusketchId?: string;
	// boolean variable to prevent race-conditions when deciding which process queue item should
	// trigger post-processing
	hasReachedPostProcessing?: boolean;
}

export interface CustomSystemPrompt {
	noteType: NoteType;
	// personality, goal, and guardrails are the 3 parts of the system prompt
	personality: string;
	goal: string;
	guardrails: string;
	organizationId: string;
}

export enum EstimateType {
	MITIGATION = "MITIGATION",
	RECONSTRUCTION = "RECONSTRUCTION",
	CONTENTS = "CONTENTS",
	MOLD = "MOLD",
	FIRE_MITIGATION = "FIRE_MITIGATION",
	BIOHAZARD = "BIOHAZARD",
	TIME_AND_MATERIAL = "TIME_AND_MATERIAL",
}

// NoteType exists because we need different prompts for Exterior & Interior Conversations.
// They're both RECONSTRUCTION estimate types, but we need to differentiate them in the UI for
// the prompts sake right now.
export type NoteType = "Mitigation" | "Interior Reconstruction" | "Exterior Reconstruction" | "Contents" | "Mold";

export enum EstimateCreationSource {
	REBUILD = "REBUILD", // NOT USED ANYMORE - LEFT IN FOR BACKWARDS COMPATIBILITY.
	REBUILD_WEB = "REBUILD_WEB",
	REBUILD_MOBILE = "REBUILD_MOBILE",
	XCELERATE = "XCELERATE",
	GOOGLE_FORM = "GOOGLE_FORM",
	JOB_NIMBUS = "JOB_NIMBUS",
}

export enum EstimateReferenceNumberType {
	UNKNOWN = "UNKNOWN",
	XCELERATE = "XCELERATE",
	JOB_NIMBUS = "JOB_NIMBUS",
}

export enum ScopeTag {
	water_mitigation = "water_mitigation",
	interior_reconstruction = "interior_reconstruction",
	exterior_reconstruction = "exterior_reconstruction",
	contents = "contents",
	mold = "mold",
	fire_mitigation = "fire_mitigation",
	biohazard = "biohazard",
	time_and_material = "time_and_material",
}

export class EExtraDataMappingType {
	// errors - Make sure to add the type to the isError function when adding a new error type
	static readonly NoEmptyValueNoDefault = "no-empty-value-no-default";
	static readonly NotAllMappingsUsed = "not-all-mappings-used";
	static readonly UnitNotMatching = "unit-not-matching";
	static readonly InputWithNoMapping = "input-with-no-mapping";
	static readonly QuantityOutOfRange = "quantity-out-of-range";
	static readonly QuantityOutOfRangeTPARule = "quantity-out-of-range-tpa-rule";
	static readonly LowConfidenceInput = "low-confidence-input";
	static readonly NoUnit = "no-unit";
	static readonly NoQuantity = "no-quantity";
	static isError(type: string): boolean {
		return (
			type === EExtraDataMappingType.NoEmptyValueNoDefault ||
			type === EExtraDataMappingType.NotAllMappingsUsed ||
			type === EExtraDataMappingType.UnitNotMatching ||
			type === EExtraDataMappingType.InputWithNoMapping ||
			type === EExtraDataMappingType.LowConfidenceInput ||
			type === EExtraDataMappingType.QuantityOutOfRange ||
			type === EExtraDataMappingType.QuantityOutOfRangeTPARule ||
			type === EExtraDataMappingType.NoUnit ||
			type === EExtraDataMappingType.NoQuantity
		);
	}

	// modifers
	static readonly QuantityModifier = "quantity-modifier";
}

export interface IMappingExtraData {
	rnrIndex?: number;
	rnrType?: string;
	type?: string;
	variablesMapping?: VariableMapping[];
	element?: LineItemElementProps;
	mappings?: IWordMapping[];
}

export type LineItemStringPartOrMappingExtraData = string | IMappingExtraData;

export interface ILineItemMetadata {
	lineItem: IDatabaseLineItem;
	variablesMapping: IVariableMappingMini[];
	leftWordsMapping: IWordMapping[];
	rightWordsMapping: IWordMapping[];
	outLineItems: LineItemStringPartOrMappingExtraData[][];
}
export interface IEstimateMetadata {
	leftWordsMappingArray: IWordMapping[][];
	rightWordsMappingArray: IWordMapping[][];
	allExistingMappingArray: string[][];
	tupleId: string;
	tupleContent: ITupleContentById;
	databaseMapping: IDatabaseMappingMini;
	leftWordsAfterCleanup: IWordWithConfidence[];
	rightWordsAfterCleanup: IWordWithConfidence[];
	leftWordsParameterised: IWordParameterized[];
	rightWordsParameterised: IWordParameterized[];
	lineItemsMetadata: ILineItemMetadata[];
	allUnusedWordsParameterised?: IWordParameterized[];
	lowConfidenceArray?: DocumentWord[];
}

export interface IMappedLineItem {
	uniqueId: string;
	lineItem: ILineItemForUI;
	tuple: Omit<ITupleOutputForUI, "lineItems">;
}

export type LineItemDiff = {
	diff: ObjectDiff;
	dateTime: number;
} & {
	source: "rules_engine";
	ruleUuid: string;
	// cache rule name here so we don't have to query rules
	ruleName: string;
};

export interface ILineItemForUI {
	uniqueId?: string;
	isComplement?: boolean;
	isLikely?: boolean;
	$$complementTupleId?: string; // we use this to store the original tuple id for this line item when we merge it under one parent in the UI
	addedByTPARule?: boolean;
	removedByTPARule?: boolean;
	deleted?: boolean;
	lineItem: string;
	lineItemId: string;
	explanation?: string;
	qnm: IQuantityMeasurement[];
	formula?: IQuantityFormula;
	mappingExtraData: IMappingExtraData[];
	leftMapping: IWordWithConfidence[];
	rightMapping: IWordWithConfidence[];
	confirmed?: boolean;
	confidence?: number | null;
	confidenceExplanation?: string;
	orderIndex?: number;
	isMutuallyExcluded?: boolean;
	isExcludedBy?: ILineItemForUI[];
	comment?: string;
	photos?: AttachmentFile[];
	predictions?: Prediction[];
	scopeNotesMappings?: ScopeNoteMapping;
	scopeNotesMappingsList?: ScopeNoteMapping[];
	croppedImageMappingsList?: CroppedImageMapping[];
	sheetsFileMappingsList?: SheetsFileMapping[];
	scopeNoteGenerationSteps?: {
		derivedSubstring?: string;
		lineItemPermutation?: string;
		originalText?: string;
		llmAcceptableValues?: string[];
	};
	auditTrail?: LineItemDiff[];
}

// mappings to a .xlsx or .csv file with a list of cell coordinates
export interface SheetsFileMapping {
	fileId: string;
	filePath: string;
	cellCoords: CellCoordinate[];
}

export type CellCoordinate = {
	row: number;
	col: number;
};

export interface CroppedImageMapping {
	imagePath: string;
	region: Region;
}

export interface ScopeNoteMapping {
	startIndex: number;
	endIndex: number;
	// this should be a required field once we convert all existing scope notes to scope note objects
	scopeNoteUuid?: string;
	// if a line item from a new scope note object "overwrites" an existing line item form an older scope note object,
	// we want to preserve the mapping to the older scope note object but mark `isOverwritten=true` so we know that that
	// mapping isn't used anymore
	isOverwritten?: boolean;
}

export interface IMissingMappings {
	lineItem: string;
	lineItemId: string;
	room: string;
	unit: string;
	calc: string;
}

export interface ITupleOutputForUI {
	isComplementOf?: string;
	isLikely?: boolean;
	lowConfidenceArray?: DocumentWord[];
	originalTupleIndex?: number;
	tupleId: string;
	lineItems: ILineItemForUI[];
	fullPageImagePath?: string;
	tupleRegion: Region;
	hasError?: boolean;
	hadErrorThatHasBeenFixed?: boolean;
	deleted?: boolean;
	originalInput?: {
		left: IWordWithConfidence[];
		right: IWordWithConfidence[];
		selections: string[];
	};
	orederIndex?: number;
	isFreeFormFlag?: boolean;
	_aiReasoning?: string;
	_improvement?: Record<string, unknown>; // this is used to store the improvement data from the AI
	// temporary workaround for allowing tuples to access data from
	// a different room (used when tuples are moved to a new page)
	pageMetadata?: IMetadataForFinalEstimate;
	scopeNoteSubstring?: string;
}

export interface IWordParameterized {
	confidence?: number;
	parameter: string;
	word: string;
	id?: number;
}

export interface IWordMapping extends IWordParameterized {
	mapping?: IVariableMappingMini;
	usedInCreation?: boolean;
}

export interface IAcceptedValue extends IWordMapping {
	value: string;
}

export interface IQuantityMeasurement {
	error?: string;
	measurement: string;
	quantity: number;
	modifier?: string;
	originalQNMBeforeRollup?: IQuantityMeasurement;
}

export interface ITupleContentById {
	id: string;
	selectedWords: string[];
	azureLeftWords: DocumentWord[];
	azureRightWords: DocumentWord[];

	// For Tic Sheets that only have one cell per tuple,
	// we put the content in the right cell and leave the left cell undefined
	templateTupleIndex: number;
	azureLeftCell: DocumentTableCell | undefined;
	azureRightCell: DocumentTableCell;
	templateLeftCell: TemplateCell | undefined;
	templateRightCell: TemplateCell;
	// templateTuple: TemplateCellTuple;
	azureLeftRegion?: Region;
	azureRightRegion?: Region;
	azureFullRegion?: Region;
	azureLeftRegionBase64?: string;
	azureRightRegionBase64?: string;
	azureFullRegionBase64?: string;
}

/**
 * Very similar to ITupleContentById but doesn't have Template values
 * (Template doesn't exist for free form content)
 */
export interface IFreeFormContentById {
	id: string;
	selectedWords: string[];
	azureLeftWords: DocumentWord[];
	azureRightWords: DocumentWord[];

	azureLeftCell: DocumentTableCell;
	azureRightCell: DocumentTableCell;

	azureLeftRegion?: Region;
	azureRightRegion?: Region;
	azureFullRegion?: Region;
	azureLeftRegionBase64?: string;
	azureRightRegionBase64?: string;
	azureFullRegionBase64?: string;
}

export enum QueuedItemToProcessStatus {
	None = 0,
	Queued = 10,
	Extracted = 20, // images extraction from pdf
	Classified = 30, // images classification and orientation fix
	LayoutAndReadExtracted = 40, //Azure layout and text reading
	CellsExtracted = 50, //cell extraction from layout and reading
	RawDataFromCellsExtracted = 60, //extract raw data from cells
	CellWithInputExtracted = 70, // Identify and extract cells with input
	CellImproveWithOpenAICompleted = 80, // use openAI to improve content extraction ( selection box etc )
	LineItemDatabaseCreated = 90, // activate mapping algorithm on cells and create a line items metadata database
	ComplementLineItemsCreated = 91, // create complement line items
	UILineItemDatabaseCreated = 93, // convert line items metadata database to UI friendly format
	FinalEstimateCreated = 97, // create final estimate
	Completed = 100, //'100-completed',
	CompletedNotProcessed = 110, // '110-completed-not-processed',
	CompletedWithError = 120, //'120-completed-with-error',
	CompletedActingAsATracker = 130, //'130-completed-acting-as-a-tracker',
}

export function getQueuedItemToProcessStatusName(status: QueuedItemToProcessStatus): string {
	switch (status) {
		case QueuedItemToProcessStatus.None:
			return "None";
		case QueuedItemToProcessStatus.Queued:
			return "Queued";
		case QueuedItemToProcessStatus.Extracted:
			return "Extracted";
		case QueuedItemToProcessStatus.Classified:
			return "Classified";
		case QueuedItemToProcessStatus.LayoutAndReadExtracted:
			return "LayoutAndReadExtracted";
		case QueuedItemToProcessStatus.CellsExtracted:
			return "CellsExtracted";
		case QueuedItemToProcessStatus.RawDataFromCellsExtracted:
			return "RawDataFromCellsExtracted";
		case QueuedItemToProcessStatus.CellWithInputExtracted:
			return "CellWithInputExtracted";
		case QueuedItemToProcessStatus.CellImproveWithOpenAICompleted:
			return "CellImproveWithOpenAICompleted";
		case QueuedItemToProcessStatus.LineItemDatabaseCreated:
			return "LineItemDatabaseCreated";
		case QueuedItemToProcessStatus.Completed:
			return "Completed";
		case QueuedItemToProcessStatus.CompletedNotProcessed:
			return "CompletedNotProcessed";
		case QueuedItemToProcessStatus.CompletedWithError:
			return "CompletedWithError";
		case QueuedItemToProcessStatus.UILineItemDatabaseCreated:
			return "UILineItemDatabaseCreated";
		case QueuedItemToProcessStatus.FinalEstimateCreated:
			return "FinalEstimateCreated";
	}
	return "Unknown";
}

export interface LineItemElementProps {
	value: string;
	type: "text" | "variable";
	acceptableValues?: string[];
	allowEmptyValue?: boolean;
	conditional?: boolean;
	defaultValue?: string;
}
export interface LineItemVariable extends LineItemElementProps {
	acceptableValues: string[]; // make non optional
}

export interface IDatabaseLineItem {
	category?: string;
	elements: LineItemElementProps[];
	originalLineItem: string;
	id: string;
	deleted?: boolean;
	orderIndex?: number;
	createTime?: number;
	scope?: string; // DEPRECATED - will remove once migrated.
	scopes?: string[];
	metadata?: LineItemMetadata;
}

/**
 * Line Item metadata that is helpful during processing.
 */
export interface LineItemMetadata {
	uniqueShortName?: string;
	keyPhrases?: string;
	explanation?: string;
	units?: string;
	defaultQuantity?: string;
}

export type AIMatchings = { [s: string]: IAIMatch };
export interface IAIMatch {
	variable?: string;
	confidence: "confident" | "not confident";
}
export interface VariableMapping {
	// aiMatch: IAIMatch;
	accepted: boolean;
	deleted: boolean;
	dotColor: string;

	variableValue?: string;
	extraVariableValues?: string[];
	quantityType?: string;
	quantityValue?: string;
	measurement?: string;
	ticInput: string;
}
// Used outside the tools
export interface IVariableMappingMini {
	variableValue?: string;
	extraVariableValues?: string[];
	quantityType?: string;
	quantityValue?: string;
	measurement?: string;
	ticInput: string;
}

export enum EExtraVariableValues {
	NotAvailable = "not available",
	TicSheetError = "Mark as tic sheet error",
	CreateNewVariable = "Add new variable",
	UseLineMarker = "Use Line Marker",
	AcceptVariableDependency = "Accept Dependency",
	AcceptInvertedVariableDependency = "Accept Inverted Dependency",
}
export declare type VariableMappingMap = { [key: string]: VariableMapping[] };
export declare type VariableMappingMiniMap = {
	[key: string]: IVariableMappingMini[];
};
export declare type AIMatchingsMap = { [key: string]: AIMatchings };

export interface IComplementLineItemRule {
	maxQuantity?: number;
	minQuantity?: number;
	explanation?: string;
	selectedVariabls?: { name: string; index: number }[];
	exclusionSelectedVariabls?: { name: string; index: number }[];
	targetVariables?: { name: string; index: number; elementIndex?: number }[];
	isLikely?: boolean;
	isSpecialTPAActivationComplements?: boolean;
	defaultMeasurement?: string;
	defaultQuantity?: number;
	confidence?: number;
}
export type ComplementLineItemRulesMap = { [key: string]: IComplementLineItemRule };
export type ComplementLineItemRulesMapNew = { [key: string]: IComplementLineItemRule[] };
export interface IDatabaseMapping {
	minQuantity?: { [s: string]: number };
	maxQuantity?: { [s: string]: number };
	overrideDefaultValues?: { [s: string]: string };
	defaultQuantity?: number;
	defaultMeasurement?: string;
	variableDependency?: ("dependent" | "inverted" | "independent")[];
	notes: string;
	variablesMapping: VariableMappingMap;
	complementLineProperties?: ComplementLineItemRulesMap;
	complementLinePropertiesNew: ComplementLineItemRulesMapNew;
	selectedLineItemsID: string[];
	selectedLineItems?: IDatabaseLineItem[];

	selectedComplementLineItemsID: string[];
	selectedComplementLineItems?: IDatabaseLineItem[];
	tupleID: string;
	// aiMatchings: AIMatchingsMap;
	ticType: string;
	state: "new" | "verified" | "unverified";
	badScans?: string[];
	allowedText?: string[];
}
export interface IAlternativeMaximum {
	tupleID: string;
	maxQuantity: number;
	measurement: string;
	explanation: string;
}
export interface IXactimateAlternativeMapping {
	source: string;
	dest: string;
}

export enum EDrywallWidthCalculation {
	W = "W",
	W_AffectedMaterial = "W - affected material",
}
export enum PFWidthCalculation {
	PF = "PF",
	PF_AffectedMaterial = "PF - affected material",
}
export enum PCWidthCalculation {
	PC = "PC",
	PC_AffectedMaterial = "PC - affected material",
}

export interface ITPARule {
	tpa: string;
	carrier: string;
	ruleType: string;
	rules: {
		drywallPatchWithSize_affected_material: string[];
		drywallFloodCuts_affected_material: string[];
		drywall_continuous_surface: string[];
		drywall1_2_5_8_affected_material: string[];
		drywallPatchWithSize: boolean;
		drywallWidthType: EDrywallWidthCalculation;
		baseboardsWidthType?: PFWidthCalculation;
		chairRailWidthType?: PCWidthCalculation;
		crownMoldingWidthType?: PFWidthCalculation;
		trimBoardWidthType?: PFWidthCalculation;
		conRoomToConLabType?: string;
		conRoomToConLabTimeValue?: string;
		baseboards?: boolean;
		baseboards_affected_material?: string[];
		baseboards_continuous_surface?: string[];
		crownMolding?: boolean;
		crownMolding_affected_material?: string[];
		crownMolding_continuous_surface?: string[];
		chairRail?: boolean;
		chairRail_affected_material?: string[];
		chairRail_continuous_surface?: string[];
		trimBoard?: boolean;
		trimBoard_affected_material?: string[];
		trimBoard_continuous_surface?: string[];
		drywallFloodCuts: boolean;
		drywall1_2_5_8: boolean;
		dryWallPatchSize: number;
		alternativeMaximums: IAlternativeMaximum[];
		alternativeXAMappings: IXactimateAlternativeMapping[];
		rollupType: string;
		rollupPercentage: number;
		rollupApplied: string[];
		rollupAppliedIDs: string[];
		selectedTuples: string[];
		selectedComplementLineItems: string[];
		selectedComplementLineItemsRules: string[];
		expandedTupleIDS: string[];
		splitLineItems: string[];
	};
}

// Used outside the tools
export interface IDatabaseMappingMini {
	overrideDefaultValues?: { [s: string]: string };
	minQuantity?: { [s: string]: number };
	maxQuantity?: { [s: string]: number };
	defaultQuantity?: number;
	defaultMeasurement?: string;
	variableDependency?: ("dependent" | "inverted" | "independent")[];
	complementLineRules?: ComplementLineItemRulesMapNew;
	variablesMapping: VariableMappingMiniMap;
	selectedLineItemsID: string[];
	selectedLineItems: IDatabaseLineItem[];
	selectedComplementLineItemsID: string[];
	selectedComplementLineItems: IDatabaseLineItem[];
	tupleID: string;
	ticType: string;
	badScans?: string[];
	allowedText?: string[];
}

export interface IRoomAndIssuesInfo {
	room: IPerRoomFinalEstimate;
	issues: ITupleOutputForUI[];
}
/**
 * Interface for confidence improvement results - updated for variable selection focus
 */
export interface IConfidenceImprovementResult {
	// Always present in prompt output
	readableText: string;
	confidence: number;
	shouldKeepAsIssue: boolean;
	reasoning: string;

	// Present in low confidence prompt
	matchesLineItem?: boolean;
	userSelectedValues?: string[];
	lowConfidenceAnalysis?: {
		potentialMisreads: Array<{
			detectedWord: string;
			likelyActualTerm: string;
			isAppliedInLineItem: boolean;
			actualWordsFoundInLineItem: string;
		}>;
		hasUnresolvedMisreads: boolean;
	};

	// Freeform prompt
	isFreeFormFlag?: boolean;
	isTemplateOnly?: boolean;

	// User input detection
	hasUserInput?: boolean;
	userInput?: string;
}

export interface IAnalytics {
	totalIssues: number;
	lowConfidenceIssues: number;
	freeFormIssues: number;
	startTime: number;
	falsePositivesDetected: number;
	confidenceImproved: number;
	originalIssueCount?: number;
	updatedIssueCount?: number;
	issuesResolved?: number;
	improvementPercentage?: number;
	processingTimeMs?: number;
	falsePositiveRate?: number;
	endTime?: number;
}

export interface ClusterToRoomMapping {
	clusterId: string;
	roomId: string;
	isMappedByUser?: boolean;
}

export interface Prediction {
	sourceUploadId?: string;
	objectId?: string;
	confidence?: number;
	boundingBox?: Region;
	model?: "gpt-4o" | "rebuild-od-v1";
}

/**
 * Scope of work data for PDF export is either summarized per category or
 * itemized per room right now.  This makes up the bulk of the PDF export.
 *
 * In the future, we can make these templates more customizable.
 */
export type ScopeOfWorkData =
	| {
			type: "category-grouped";
			scopeOfWorkCategories: {
				category: string;
				summary: string;
			}[];
			textOverride?: string; // when a user overrides the default text it's free-text form.
	  }
	| {
			type: "room-grouped";
			scopeOfWorkRooms: ItemizedRoomPriceBreakdown[];
	  };

export type PDFPreviewData = {
	title: string;
	bannerImageUrl: string;
	bannerImageScale?: number; // Scale factor for banner image (e.g., 0.75 for 75%)
	bannerImageAlignment?: "left" | "center" | "right"; // Horizontal alignment of banner image
	footerImageUrl?: string;
	headerInfo: HeaderInfoItem[];
	organizationName: string;
	scopeOfWorkTitle: string;
	scopeOfWorkCategories: ScopeOfWorkData;
	totalCostEstimate: string;
	subtotal?: string; // Base price before overhead and profit (only for Reconstruction)
	overhead?: string; // 10% overhead amount (only for Reconstruction)
	profit?: string; // 10% profit amount (only for Reconstruction)
	taxAmount?: string; // Tax amount (only included if tax > 0)
	terms?: string;
	footerLines?: string[];
	topLeftText?: string[];
};

export type ItemizedRoomPriceBreakdown = {
	room: string;
	price: number;
	categories: ItemizedCategoryPriceBreakdown[];
};

export type ItemizedCategoryPriceBreakdown = {
	category: string;
	price: number;
	items: ItemWithPrice[];
};

export type ItemWithPrice = {
	item: string;
	price: number;
};

export type EstimateExport = {
	type: "Xactimate" | "PDF";
	timestamp: number;
	xactimateTransactionId?: string;
	pdfExportStoragePath?: string;
};

export type RuleTemplateInputField = {
	name: string;
	description?: string;
} & (
	| {
			type: "array";
			items: RuleTemplateInputField[];
	  }
	| { type: "object"; properties: RuleTemplateInputField[] }
	| { type: "enum"; values: string[]; isMultiSelect?: boolean }
	| {
			type: "lineItem";
			parameters?: {
				sourceLineItemPath?: string;
				disablePermutationSelection?: boolean;
				enablePartialSelection?: boolean;
			};
	  }
	| { type: "boolean" | "string" | "number" | "organizationId" | "tpa" | "carrier" }
);

export type RuleTemplateId =
	| "complements_and_recommendations"
	| "line_item_transformation"
	| "mutually_exclusive"
	| "update_after_hours"
	| "merge_complements";

export type RuleTemplate = {
	name: string;
	id: RuleTemplateId;
	enabled?: boolean;
	inputField: {
		name: "ruleInputs";
		type: "object";
		properties: RuleTemplateInputField[];
	};
};

export type RulesEngineCatOptions = "1" | "2" | "3";

export type RuleRunCriteria = {
	organizationId?: string;
	catNumber?: RulesEngineCatOptions;
	estimateType?: EstimateType;
	tpa?: string;
	carrier?: string;
};

export type Rule = {
	uuid: string;
	name: string;
	isDev: boolean;
	isDisabled?: boolean;
	runCriteria: RuleRunCriteria;
} & (
	| {
			templateId: "complements_and_recommendations";
			ruleInputs: ComplementsRuleInputs;
	  }
	| {
			templateId: "line_item_transformation";
			ruleInputs: TransformationRuleInputs;
	  }
	| {
			templateId: "mutually_exclusive";
			ruleInputs: MutuallyExclusiveRuleInputs;
	  }
	| {
			templateId: "update_after_hours";
			ruleInputs: UpdateAfterHoursRuleInputs;
	  }
	| {
			templateId: "merge_complements";
			ruleInputs: MergeComplementsRuleInputs;
	  }
);

export type RuleInputLineItem = {
	lineItemId: string;
	selectedValues?: Record<string, string[]>;
	selectedPermutation?: string;
};

export type ComplementsRuleOverrides = {
	permutation?: RuleInputLineItem;
	doNotCreateChild?: boolean;
	child?: ComplementsRuleChild;
	roomTypes?: RoomType[];
	runCriteria?: RuleRunCriteria;
};

export type ComplementsRuleInputs = {
	parent: RuleInputLineItem;
	child: ComplementsRuleChild;
	roomTypes?: RoomType[];
	overrides?: ComplementsRuleOverrides[];
};

// the actual transformations applied to a line item after it has matched the run criteria
export type TransformationRuleOptions = {
	quantityConversionType?: TransformationQuantityFunction;
	quantityConversionValue?: string;
	shouldRemove?: boolean;
	finalLineItem?: RuleInputLineItem;
};

export type TransformationRuleOverrides = TransformationRuleOptions & {
	permutation?: RuleInputLineItem;
};

export type TransformationRuleInputs = TransformationRuleOptions & {
	initialLineItem?: RuleInputLineItem;
	overrides?: TransformationRuleOverrides[];
};

export type MutuallyExclusiveRuleInputs = {
	excluders: { excluder: RuleInputLineItem }[];
	excludee: RuleInputLineItem;
};

export type TimeBlock = {
	day: DayOfWeek;
	startTime: number;
	endTime: number;
};

export type UpdateAfterHoursRuleInputs = { normalWorkHours?: TimeBlock[]; holidays?: { holiday: Holiday }[] };

export type MergeComplementsRuleOverrides = {
	lineItem: RuleInputLineItem;
	quantityMergeType?: MergeQuantityFunction;
	quantityMergeValue?: string;
};

export type MergeComplementsRuleInputs = {
	overrides?: MergeComplementsRuleOverrides[];
};

export type ComplementsQuantityFunction = "INHERIT" | "MULTIPLY" | "USE_DEFAULT";

export type TransformationQuantityFunction = "MAX_VALUE" | "MULTIPLY" | "USE_DEFAULT";

export type MergeQuantityFunction = "USE_DEFAULT";

export type ComplementsRuleChild = {
	quantityValue?: string;
	isLikely?: boolean;
	// this should be spelled "isComplementIfMitToRecon", but there is a type in our rule template object, so we ended up saving
	// a bunch of rules with the wrong spelling
	isComplimentIfMitToRecon?: boolean;
	inheritParentAction?: boolean;
	lineItem?: RuleInputLineItem;
	quantityFunction?: ComplementsQuantityFunction;
};

export type ObjectDiff = {
	path: string;
	type: "added" | "removed" | "modified" | "type-changed";
	oldValue?: any;
	newValue?: any;
};
